import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import React, { ReactNode, useState } from "react";

import CustomAlert from "../alerts/CustomAlert";

import FormLoader from "./skeleton/form-skeleton";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

type TProps = {
  isFormDirty?: boolean;
  isOpen: boolean;
  toggleModal: () => void;
  children: React.ReactNode;
  className?: string;
  trigger?: React.ReactNode;
  isLoading?: boolean;
} & (
  | { title?: string; subtitle?: string; header?: never }
  | { title?: never; subtitle?: string; header?: ReactNode }
);

const FormDialog: React.FC<TProps> = ({
  isFormDirty,
  isOpen,
  toggleModal,
  trigger,
  children,
  className,
  isLoading,
  ...props
}) => {
  const [warning, setWarning] = useState(false);

  const handleModalClose = (): void => {
    if (isFormDirty) {
      setWarning(true);
    } else {
      toggleModal();
    }
  };

  const renderChildren = (): React.ReactNode => {
    if (isLoading) {
      return <FormLoader />;
    } else if (props.title) {
      return (
        <div>
          <DialogHeader className='space-y-4'>
            <DialogTitle className='text-2xl font-normal text-center Capitalize'>
              {props.title} <br />
              <p className='my-1 text-sm text-center opacity-40'>
                {props?.subtitle || ""}
              </p>
            </DialogTitle>
          </DialogHeader>
          {children}
        </div>
      );
    } else {
      return (
        <>
          {props.header}
          {children}
        </>
      );
    }
  };

  return (
    <div className='relative'>
      <Dialog open={isOpen} onOpenChange={handleModalClose}>
        <VisuallyHidden>
          <DialogTitle />
          <DialogDescription />
        </VisuallyHidden>
        {trigger && trigger}
        <DialogContent
          className={cn(
            "h-fit p-0 max-w-screen-2xl md:max-w-2xl flex flex-col justify-between",
            className,
          )}
        >
          {renderChildren()}
        </DialogContent>
      </Dialog>

      <CustomAlert
        isOpen={warning}
        setIsOpen={setWarning}
        btnAction={toggleModal}
        title='Are you sure?'
        desc='If you proceed, this action cannot be undone. Are you sure you want to continue? Form data will be lost.'
        cancelName='No, Continue Editing'
        btnName='Yes, Cancel'
      />
    </div>
  );
};

export default FormDialog;
